import type { components } from '@fluencelabs/vodopad-admin-api'

type ErrorBody = components['schemas']['ErrorBody']

type ApiResultLike<TData = unknown, TError = unknown> = {
  response: Response
  data?: TData
  error?: TError
}

type ApiResultBase<TError = unknown> = Pick<
  ApiResultLike<unknown, TError>,
  'response' | 'error'
>

export class ApiError extends Error {
  readonly statusCode: number

  constructor(message: string, statusCode: number) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
  }
}

function isErrorBody(error: unknown): error is ErrorBody {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'string'
  )
}

function getApiErrorMessage<TData, TError>(
  result: ApiResultLike<TData, TError>,
  fallbackMessage: string,
) {
  const error = result.error

  if (isErrorBody(error) && error.error.trim()) {
    return error.error
  }

  if (result.response.statusText) {
    return result.response.statusText
  }

  return fallbackMessage
}

export function unwrapApiResult<TResult extends ApiResultBase>(
  result: TResult,
  fallbackMessage = 'Request failed',
) {
  if (!result.response.ok) {
    throw new ApiError(
      getApiErrorMessage(result, fallbackMessage),
      result.response.status,
    )
  }

  return result
}

export function unwrapApiData<TData = unknown, TError = unknown>(
  result: ApiResultLike<TData, TError>,
  fallbackMessage = 'Request failed',
) {
  const unwrapped = unwrapApiResult(result, fallbackMessage)

  if (unwrapped.data === undefined) {
    throw new Error(fallbackMessage)
  }

  return unwrapped.data
}
